import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { getDevToken, clearDevToken } from '../composables/useAuth'
import { trackPageView } from '../services/analytics'
import Login from '../views/Login.vue'
import Signup from '../views/Signup.vue'
import AuthCallback from '../views/AuthCallback.vue'
import SupplierDashboard from '../views/SupplierDashboard.vue'
import SupplierCompanyProfile from '../views/SupplierCompanyProfile.vue'
import SupplierMetricsCatalog from '../views/SupplierMetricsCatalog.vue'
import AdvisorSupplierDirectory from '../views/AdvisorSupplierDirectory.vue'
import AdvisorSupplierProfile from '../views/AdvisorSupplierProfile.vue'
// AdvisorDashboard import removed — /advisor/dashboard now redirects to /advisor/today
import SupplierProducts from '../views/SupplierProducts.vue'

// Stubs
import SupplierPromotions from '../views/stubs/SupplierPromotions.vue'
import SupplierTraining from '../views/stubs/SupplierTraining.vue'
import AdvisorProducts from '../views/stubs/AdvisorProducts.vue'
import AdvisorTraining from '../views/stubs/AdvisorTraining.vue'
import ResourcesDocs from '../views/stubs/ResourcesDocs.vue'
import ResourcesSupport from '../views/stubs/ResourcesSupport.vue'
import ChangePassword from '../views/ChangePassword.vue'

// Custom Auth Guard that supports both Auth0 SDK and our local dev ROPG token
const customAuthGuard = async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const userStore = useUserStore()
    const { isAuthenticated, isLoading } = useAuth0()

    // Wait for Auth0 SDK to finish initializing (prevents double-redirect)
    let maxWait = 50 // 5 seconds max
    while (isLoading.value && maxWait > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
        maxWait--
    }

    // 1. If already authenticated via Auth0 SDK, let it pass
    if (isAuthenticated.value) {
        return next()
    }

    // 2. If authenticated via local store (dev-login ROPG), let it pass
    if (userStore.isAuthenticated) {
        return next()
    }

    // 3. Try to restore from stored dev-login token (survives page reloads)
    const devToken = getDevToken()
    if (devToken) {
        try {
            // First, try to fetch the existing user with the token
            // This is more reliable than initUser on page reload since the user already exists
            await userStore.fetchUser(devToken)
            if (userStore.isAuthenticated) {
                return next()
            }
        } catch (fetchError) {
            // fetchUser failed, try initUser as fallback (for fresh logins)
            console.warn('Dev token fetchUser failed, trying initUser:', fetchError)
            try {
                const payload = devToken.split('.')[1]
                if (!payload) throw new Error('Invalid token format')
                const claims = JSON.parse(atob(payload))
                const email = claims.email || claims.sub
                await userStore.initUser(devToken, email, null)
                if (userStore.isAuthenticated) {
                    return next()
                }
            } catch (initError) {
                // Both methods failed - token might be expired or invalid
                // Clear the invalid token to prevent infinite loops
                console.warn('Dev token restore failed completely:', initError)
                clearDevToken()
            }
        }
    }

    // 4. Not authenticated — redirect to our custom login page
    return next({
        path: '/login',
        query: to.fullPath !== '/' ? { returnTo: to.fullPath } : undefined
    })
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        // --- AUTH ROUTES ---
        {
            path: '/',
            name: 'home',
            component: Login
        },
        {
            path: '/login',
            name: 'login',
            component: Login
        },
        {
            path: '/signup',
            name: 'signup',
            component: Signup,
            beforeEnter: customAuthGuard
        },
        {
            path: '/auth/callback',
            name: 'auth-callback',
            component: AuthCallback
        },
        {
            path: '/claim',
            name: 'claim',
            redirect: to => ({ path: '/signup', query: { claim: to.query.code } })
        },
        {
            path: '/invite',
            name: 'invite',
            component: () => import('../views/AcceptInvite.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/change-password',
            name: 'change-password',
            component: ChangePassword,
            beforeEnter: customAuthGuard,
            meta: { requiresAuth: true }
        },

        // --- ONBOARDING ROUTES ---
        {
            path: '/advisor/onboarding',
            name: 'advisor-onboarding',
            component: () => import('../views/AdvisorOnboarding.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/supplier/onboarding',
            name: 'supplier-onboarding',
            component: () => import('../views/SupplierOnboarding.vue'),
            beforeEnter: customAuthGuard
        },

        // --- ADMIN ROUTES ---
        {
            path: '/admin',
            name: 'admin-dashboard',
            component: () => import('../views/admin/AdminDashboard.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/suppliers',
            name: 'admin-suppliers',
            component: () => import('../views/admin/AdminSuppliers.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/suppliers/:supplierId',
            name: 'admin-supplier-detail',
            component: () => import('../views/admin/AdminSupplierDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/users',
            name: 'admin-users',
            component: () => import('../views/admin/AdminUsers.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/ai-diagnostics',
            name: 'admin-ai-diagnostics',
            component: () => import('../views/admin/AdminAiDiagnostics.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/ai-models',
            name: 'admin-ai-models',
            component: () => import('../views/admin/AdminAiModels.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/ai-models/:id',
            name: 'admin-ai-model-detail',
            component: () => import('../views/admin/AdminAiModelDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/ai-prompts',
            name: 'admin-ai-prompts',
            component: () => import('../views/admin/AdminAiPrompts.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/tsds',
            name: 'admin-tsds',
            component: () => import('../views/admin/AdminTSDs.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/taxonomy',
            name: 'admin-taxonomy',
            component: () => import('../views/admin/AdminTaxonomy.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/quotes',
            name: 'admin-quote-queue',
            component: () => import('../views/admin/AdminQuoteQueue.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/invite-codes',
            name: 'admin-invite-codes',
            component: () => import('../views/admin/AdminInviteCodes.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/admin/create-user',
            name: 'admin-create-user',
            component: () => import('../views/admin/AdminCreateUser.vue'),
            beforeEnter: customAuthGuard
        },

        // --- SUPPLIER ROUTES ---
        {
            path: '/supplier',
            name: 'supplier-dashboard',
            component: SupplierDashboard,
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'BASIC' }
        },
        {
            path: '/supplier/profile',
            name: 'supplier-profile',
            component: SupplierCompanyProfile,
            beforeEnter: customAuthGuard
        },
        {
            path: '/supplier/products',
            name: 'supplier-products',
            component: SupplierProducts,
            beforeEnter: customAuthGuard
        },
        {
            path: '/supplier/solution-map',
            name: 'supplier-solution-map',
            component: () => import('../views/SupplierSolutionMap.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/products/new',
            name: 'supplier-product-create',
            component: () => import('../views/SupplierProductEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/products/:productId/edit',
            name: 'supplier-product-edit',
            component: () => import('../views/SupplierProductEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/promotions',
            name: 'supplier-promotions',
            component: SupplierPromotions,
            beforeEnter: customAuthGuard
        },
        {
            path: '/supplier/training',
            name: 'supplier-training',
            component: SupplierTraining,
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/training/new',
            name: 'supplier-training-create',
            component: () => import('../views/SupplierTrainingEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/metrics',
            name: 'supplier-metrics',
            component: SupplierMetricsCatalog,
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/settings',
            name: 'supplier-settings',
            component: () => import('../views/SupplierSettings.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'BASIC' }
        },
        {
            path: '/supplier/contacts',
            name: 'supplier-contacts',
            component: () => import('../views/SupplierContacts.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/supplier/team',
            name: 'supplier-employees',
            component: () => import('../views/SupplierEmployees.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'BASIC' }
        },
        {
            path: '/supplier/products/:productId',
            name: 'supplier-product-detail',
            component: () => import('../views/SupplierProductDetail.vue'),
            beforeEnter: customAuthGuard
        },
        // Quote Routes (Phase 9)
        {
            path: '/supplier/quotes',
            name: 'supplier-quote-queue',
            component: () => import('../views/SupplierQuoteQueue.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/quotes/:quoteId',
            name: 'supplier-quote-detail',
            component: () => import('../views/SupplierQuoteDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiredTier: 'ENTERPRISE' }
        },

        // --- ADVISOR ROUTES ---

        // Advisor Workspace (v3b)
        {
            path: '/advisor/workspace/deals',
            name: 'workspace-pipeline',
            component: () => import('../views/WorkspacePipeline.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/quick-match',
            name: 'workspace-quick-match',
            component: () => import('../views/WorkspaceQuickMatch.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts',
            name: 'workspace-accounts',
            component: () => import('../views/WorkspaceAccountsList.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/new',
            name: 'workspace-new-account',
            component: () => import('../views/WorkspaceNewAccount.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:id',
            name: 'workspace-account',
            component: () => import('../views/WorkspaceAccount.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/context',
            name: 'workspace-account-context',
            component: () => import('../views/WorkspaceAccountContext.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/meeting',
            name: 'workspace-meeting-companion',
            component: () => import('../views/WorkspaceMeetingCompanion.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/post-meeting',
            name: 'workspace-post-meeting',
            component: () => import('../views/WorkspacePostMeeting.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/questions',
            name: 'workspace-account-questions',
            component: () => import('../views/WorkspaceAccountQuestions.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/questions/print',
            name: 'workspace-account-questions-print',
            component: () => import('../views/WorkspaceAccountQuestionsPrint.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/prep',
            name: 'workspace-meeting-prep',
            component: () => import('../views/WorkspaceMeetingPrep.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/suppliers/:supplierId/analysis',
            name: 'workspace-account-supplier-analysis',
            component: () => import('../views/WorkspaceSupplierAnalysis.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:accountId/solutions/:solutionId',
            name: 'workspace-solution',
            component: () => import('../views/WorkspaceSolutionWorkspace.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/accounts/:id/create-deal',
            name: 'workspace-create-deal',
            component: () => import('../views/WorkspaceCreateDeal.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:id',
            name: 'workspace-deal',
            component: () => import('../views/WorkspaceDeal.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:id/context',
            name: 'workspace-deal-context',
            component: () => import('../views/WorkspaceDealContext.vue'),
            beforeEnter: customAuthGuard
        },
        // Phase 1C: Solution Track Lifecycle
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId',
            redirect: to => `/advisor/workspace/deals/${to.params.dealId}/tracks/${to.params.trackId}/technology-discovery`,
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/quote-request',
            name: 'workspace-quote-request',
            component: () => import('../views/WorkspaceQuoteRequest.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/registration',
            name: 'workspace-deal-registration',
            component: () => import('../views/WorkspaceDealRegistration.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/quotes',
            name: 'workspace-quote-management',
            component: () => import('../views/WorkspaceQuoteManagement.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/proposal',
            name: 'workspace-proposal',
            component: () => import('../views/WorkspaceProposal.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/config-builder',
            name: 'workspace-config-builder',
            component: () => import('../views/WorkspaceConfigBuilder.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/config-compare',
            name: 'workspace-config-compare',
            component: () => import('../views/WorkspaceConfigCompare.vue'),
            beforeEnter: customAuthGuard
        },
        // Phase 2-4: Discovery, Signals, Prep Brief, AI Companion
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/technology-discovery',
            name: 'workspace-tech-discovery',
            component: () => import('../views/WorkspaceTechDiscovery.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/suppliers/:supplierId/analysis',
            name: 'workspace-supplier-analysis',
            component: () => import('../views/WorkspaceSupplierAnalysis.vue'),
            props: true,
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/signals',
            name: 'workspace-signals',
            component: () => import('../views/WorkspaceSignalDashboard.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/prep-brief',
            name: 'workspace-prep-brief',
            component: () => import('../views/WorkspacePrepBrief.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/ai-companion',
            name: 'workspace-ai-companion',
            component: () => import('../views/WorkspaceAiCompanion.vue'),
            beforeEnter: customAuthGuard
        },
        // Route removed — answer confirmation is now inline on activity detail
        // {
        //     path: '/advisor/workspace/deals/:dealId/confirm-answers',
        //     name: 'workspace-answer-confirmation',
        //     component: () => import('../views/WorkspaceAnswerConfirmation.vue'),
        //     beforeEnter: customAuthGuard
        // },
        {
            path: '/advisor/workspace/accounts/:accountId/activities/:activityId',
            name: 'workspace-activity-detail',
            component: () => import('../views/WorkspaceActivityDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/solution-discovery',
            name: 'workspace-solution-discovery',
            component: () => import('../views/WorkspaceSolutionDiscovery.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/workspace/tsd-contacts',
            name: 'workspace-tsd-contacts',
            component: () => import('../views/WorkspaceTsdContacts.vue'),
            beforeEnter: customAuthGuard
        },

        {
            path: '/advisor/today',
            name: 'advisor-today',
            component: () => import('../views/AdvisorToday.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/deal-intelligence',
            name: 'advisor-deal-intelligence',
            component: () => import('../views/AdvisorDealIntelligence.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/quotes',
            name: 'advisor-quotes',
            component: () => import('../views/AdvisorQuotes.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/community',
            name: 'advisor-community',
            component: () => import('../views/AdvisorCommunity.vue'),
            beforeEnter: customAuthGuard
        },
        // Legacy redirects
        { path: '/advisor/pipeline', redirect: '/advisor/deal-intelligence' },
        { path: '/advisor/registrations', redirect: '/advisor/deal-intelligence' },
        { path: '/advisor/actions', redirect: '/advisor/today' },
        // Legacy: /advisor/dashboard redirects to /advisor/today
        { path: '/advisor/dashboard', redirect: '/advisor/today' },
        {
            path: '/advisor',
            redirect: '/advisor/today',
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/suppliers',
            name: 'advisor-supplier-directory',
            component: AdvisorSupplierDirectory,
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/promotions',
            name: 'advisor-promotions',
            component: () => import('../views/AdvisorPromotions.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/suppliers/compare',
            name: 'advisor-supplier-compare',
            component: () => import('../views/AdvisorSupplierCompare.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/suppliers/:supplierId',
            name: 'advisor-supplier-profile',
            component: AdvisorSupplierProfile,
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/products',
            name: 'advisor-products',
            component: AdvisorProducts,
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/products/:productId',
            name: 'advisor-product-detail',
            component: () => import('../views/AdvisorProductDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/training',
            name: 'advisor-training',
            component: AdvisorTraining,
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/meetings',
            name: 'advisor-meetings',
            component: () => import('../views/AdvisorMeetings.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/meetings/legacy',
            name: 'advisor-meetings-legacy',
            component: () => import('../views/AdvisorMeetingsList.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/action-items',
            name: 'advisor-action-items',
            component: () => import('../views/AdvisorActionItems.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/meetings/:meetingId',
            name: 'advisor-meeting-detail',
            component: () => import('../views/AdvisorMeetingDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/product-explorer',
            name: 'advisor-product-explorer',
            component: () => import('../views/AdvisorProductExplorer.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/search',
            name: 'advisor-search',
            component: () => import('../views/AdvisorSearchResults.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/ai',
            redirect: '/advisor/today'
        },
        // Legacy: /advisor/ai-classic removed — use /advisor/today
        { path: '/advisor/ai-classic', redirect: '/advisor/today' },
        // Legacy: /advisor/ai-results removed — use /advisor/product-explorer
        { path: '/advisor/ai-results', redirect: '/advisor/product-explorer' },

        // CRM Routes - New terminology (Accounts, Contacts, Opportunities)
        {
            path: '/advisor/accounts',
            name: 'advisor-accounts',
            component: () => import('../views/AdvisorAccounts.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/contacts',
            name: 'advisor-contacts',
            component: () => import('../views/AdvisorContacts.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/channel-contacts',
            name: 'advisor-channel-contacts',
            component: () => import('../views/AdvisorChannelContacts.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/opportunities',
            name: 'advisor-opportunities',
            component: () => import('../views/AdvisorOpportunities.vue'),
            beforeEnter: customAuthGuard
        },
        // Legacy routes - redirect to new paths
        {
            path: '/advisor/companies',
            redirect: '/advisor/accounts'
        },
        // Legacy route redirects
        {
            path: '/advisor/deals',
            redirect: '/advisor/opportunities'
        },
        // v3b Mockups — Channel Advisor Workspace
        {
            path: '/advisor/v3b/pipeline',
            name: 'v3b-pipeline',
            component: () => import('../views/mockups/v3b/V3bPipelineView.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b/account/:accountId',
            name: 'v3b-account',
            component: () => import('../views/mockups/v3b/V3bAccountWorkspace.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b/deal/:dealId',
            name: 'v3b-deal',
            component: () => import('../views/mockups/v3b/V3bDealWorkspace.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b/deal/:dealId/discovery/:trackId',
            name: 'v3b-discovery',
            component: () => import('../views/mockups/v3b/V3bTechnologyDiscovery.vue'),
            meta: { requiresAuth: false }
        },

        // v3b-full Mockups — Complete Channel Advisor Workspace (18 screens)
        {
            path: '/advisor/v3b-full/pipeline',
            name: 'v3b-full-pipeline',
            component: () => import('../views/mockups/v3b-full/PipelineView.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/accounts',
            name: 'v3b-full-accounts',
            component: () => import('../views/mockups/v3b-full/AccountsList.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId',
            name: 'v3b-full-account',
            component: () => import('../views/mockups/v3b-full/AccountWorkspace.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/activity/:activityId',
            name: 'v3b-full-activity',
            component: () => import('../views/mockups/v3b-full/ActivityDetail.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/create-deal',
            name: 'v3b-full-create-deal',
            component: () => import('../views/mockups/v3b-full/CreateDeal.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId',
            name: 'v3b-full-deal',
            component: () => import('../views/mockups/v3b-full/DealWorkspace.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/confirm-answers',
            name: 'v3b-full-confirm-answers',
            component: () => import('../views/mockups/v3b-full/AnswerConfirmation.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/prep-brief',
            name: 'v3b-full-prep-brief',
            component: () => import('../views/mockups/v3b-full/DealPrepBrief.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/solution-discovery',
            name: 'v3b-full-solution-discovery',
            component: () => import('../views/mockups/v3b-full/SolutionDiscovery.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/technology-discovery',
            name: 'v3b-full-technology-discovery',
            component: () => import('../views/mockups/v3b-full/TechnologyDiscovery.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/quote-request',
            name: 'v3b-full-quote-request',
            component: () => import('../views/mockups/v3b-full/QuoteRequest.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/deal-registration',
            name: 'v3b-full-deal-registration',
            component: () => import('../views/mockups/v3b-full/DealRegistration.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/quote-management',
            name: 'v3b-full-quote-management',
            component: () => import('../views/mockups/v3b-full/QuoteManagement.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/proposal',
            name: 'v3b-full-proposal',
            component: () => import('../views/mockups/v3b-full/ProposalView.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/config-builder',
            name: 'v3b-full-config-builder',
            component: () => import('../views/mockups/v3b-full/ConfigurationBuilder.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/config-compare',
            name: 'v3b-full-config-compare',
            component: () => import('../views/mockups/v3b-full/ConfigurationComparison.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/signals',
            name: 'v3b-full-signals',
            component: () => import('../views/mockups/v3b-full/SignalDashboard.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/ai-companion',
            name: 'v3b-full-ai-companion',
            component: () => import('../views/mockups/v3b-full/AiCompanion.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/context',
            name: 'v3b-full-account-context',
            component: () => import('../views/mockups/v3b-full/AccountContext.vue'),
            meta: { requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/context',
            name: 'v3b-full-deal-context',
            component: () => import('../views/mockups/v3b-full/DealContext.vue'),
            meta: { requiresAuth: false }
        },

        {
            path: '/advisor/metrics',
            name: 'advisor-metrics',
            component: () => import('../views/AdvisorMetrics.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/assets',
            name: 'advisor-assets',
            component: () => import('../views/AdvisorAssets.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/settings',
            name: 'advisor-settings',
            component: () => import('../views/AdvisorSettings.vue'),
            beforeEnter: customAuthGuard
        },
        // Quote Routes (Phase 9)
        {
            path: '/advisor/quotes',
            name: 'advisor-quotes',
            component: () => import('../views/AdvisorQuotesList.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/quotes/new',
            name: 'advisor-quote-create',
            component: () => import('../views/AdvisorQuoteCreate.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/quotes/:quoteId',
            name: 'advisor-quote-detail',
            component: () => import('../views/AdvisorQuoteDetail.vue'),
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/opportunities/:opportunityId/compare-quotes',
            name: 'advisor-quote-compare',
            component: () => import('../views/AdvisorQuoteCompare.vue'),
            beforeEnter: customAuthGuard
        },

        // --- SHARED RESOURCES ---
        {
            path: '/resources/docs',
            name: 'resources-docs',
            component: ResourcesDocs,
            beforeEnter: customAuthGuard
        },
        {
            path: '/resources/support',
            name: 'resources-support',
            component: ResourcesSupport,
            beforeEnter: customAuthGuard
        }
    ]
})

// Tier-based route guard — redirect if supplier lacks required tier
const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, ENTERPRISE: 2 }

router.beforeEach((to) => {
    const requiredTier = to.meta.requiredTier as string | undefined
    if (!requiredTier) return true

    const userStore = useUserStore()

    // Admin impersonation bypasses tier checks
    if (userStore.isImpersonating) return true

    // Only applies to supplier users
    if (!userStore.isSupplier) return true

    const currentTier = (userStore.supplierTier as string) ?? 'FREE'
    if ((tierRank[currentTier] ?? 0) < (tierRank[requiredTier] ?? 0)) {
        // FREE tier lands on profile since dashboard is BASIC+
        return { path: currentTier === 'FREE' ? '/supplier/profile' : '/supplier' }
    }

    return true
})

// Force password change guard — redirect if user must change password
router.beforeEach((to) => {
    const allowedPaths = ['/change-password', '/login', '/logout', '/', '/auth/callback']
    if (allowedPaths.includes(to.path)) return true

    const userStore = useUserStore()
    if (userStore.user?.mustChangePassword) {
        return { path: '/change-password' }
    }

    return true
})

router.afterEach((to) => {
    trackPageView(to.path, (to.name as string) ?? undefined)

    // Dynamic page title
    const pageTitle = (to.meta.title as string) || (to.name as string) || ''
    document.title = pageTitle
        ? `${pageTitle} | Channel Standard`
        : 'Channel Standard Companion'
})

export default router
